import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ClinicalExaminationDetails from "../pages/ClinicalExamination/ClinicalExaminationDetails";
import AssignmentIcon from "@/assets/icons/assignment.svg?react";
import type { Assessment } from "../../domain/entities/Patient";
import { textStyles } from "@/lib/textStyles";
import { useParams } from "react-router-dom";
import { AssessmentStatusPill } from "@/components/AssessmentStatusPill";
import { useGetPatientAssessmentDetailQuery } from "../../infrastructure/api/patientsApi";
import { getStatusReason } from "../../infrastructure/data/PatientDetailsStatus";

interface Props {
  ongoingAssessment: Assessment | undefined;
  previousAssessments: Assessment[];
  ongoingContent?: React.ReactNode;
}

/**
 * The assessments list API returns no status, so each previous assessment
 * reads its own status from its detail query (shared with the details panel).
 */
const PreviousAssessmentStatus: React.FC<{ assessmentId: string }> = ({
  assessmentId,
}) => {
  const { patientId } = useParams<{ patientId: string }>();
  const { data } = useGetPatientAssessmentDetailQuery(
    { patientId: patientId as string, assessmentId },
    { skip: !patientId || !assessmentId },
  );
  const assessment = data?.data?.assessment;
  if (!assessment?.status) return null;

  return (
    <AssessmentStatusPill
      status={assessment.status}
      reason={getStatusReason(assessment)}
      className="mt-1 text-left"
    />
  );
};

const AssessmentList: React.FC<Props> = ({
  ongoingAssessment,
  previousAssessments,
  ongoingContent,
}) => {
  const { t } = useTranslation("patients");
  const defaultTab = ongoingAssessment ? "ongoing" : "previous";
  const [activeTab, setActiveTab] = useState<"ongoing" | "previous">(
    defaultTab,
  );
  const [selectedPreviousAssessmentId, setSelectedPreviousAssessmentId] =
    useState(previousAssessments[0]?.id ?? "");

  useEffect(() => {
    const nextTab = ongoingAssessment ? "ongoing" : "previous";
    setActiveTab(nextTab);
  }, [ongoingAssessment?.id]);

  useEffect(() => {
    const selectedStillExists = previousAssessments.some(
      (assessment) => assessment.id === selectedPreviousAssessmentId,
    );
    if (!selectedStillExists) {
      setSelectedPreviousAssessmentId(previousAssessments[0]?.id ?? "");
    }
  }, [previousAssessments, selectedPreviousAssessmentId]);

  const handleTabChange = (val: string) => {
    setActiveTab(val as "ongoing" | "previous");
  };

  const getTriggerStyle = (tabValue: "ongoing" | "previous") => ({
    ...textStyles.body,
    ...(activeTab === tabValue
      ? { color: "var(--primary)", fontWeight: 700 }
      : {}),
  });

  return (
    <div className="mb-6">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
      >
        <TabsList
          variant="line"
          className="flex h-auto w-fit max-w-full justify-start gap-6 p-0"
        >
          <TabsTrigger
            value="ongoing"
            className="max-w-[158px] flex-none cursor-pointer rounded-none px-0 pb-0 data-[state=active]:text-primary data-[state=active]:after:bg-primary"
            style={getTriggerStyle("ongoing")}
          >
            {t("assessmentList.ongoing")}
          </TabsTrigger>

          <TabsTrigger
            value="previous"
            className="max-w-[158px] flex-none cursor-pointer rounded-none px-0 pb-0 data-[state=active]:text-primary data-[state=active]:after:bg-primary"
            style={getTriggerStyle("previous")}
          >
            {t("assessmentList.previous")}
            {previousAssessments.length > 0 && (
              <span className="ml-2">{previousAssessments.length}</span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ongoing" className="mt-4">
          {ongoingAssessment ? (
            (ongoingContent ?? (
              <ClinicalExaminationDetails assessmentId={ongoingAssessment.id} />
            ))
          ) : (
            <Card className="p-6 text-center sm:p-10">
              <div className="flex flex-col items-center gap-2">
                <AssignmentIcon />
                <h3 className="text-lg font-semibold">
                  {t("assessmentList.noOngoingTitle")}
                </h3>
                <p className="max-w-md text-gray-500">
                  {t("assessmentList.noOngoingBody")}
                </p>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="previous" className="mt-4">
          {previousAssessments.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[180px_minmax(0,1fr)]">
              <div className="flex flex-wrap gap-2 lg:block">
                {previousAssessments.map((assessment, index) => {
                  const isActive = selectedPreviousAssessmentId === assessment.id;

                  return (
                    <div
                      key={assessment.id}
                      onClick={() => setSelectedPreviousAssessmentId(assessment.id)}
                      className={`shrink-0 cursor-pointer whitespace-nowrap rounded-md border px-3 py-2 lg:mb-2 lg:block lg:whitespace-normal ${
                        isActive
                          ? "border-primary bg-[#F2F7FF] text-primary"
                          : "border-transparent bg-white text-[#5F646F] hover:bg-[#F4F6F9]"
                      }`}
                      style={textStyles.body}
                    >
                      <div>
                        {t("assessmentList.assessmentLabel", {
                          number: previousAssessments.length - index,
                        })}
                      </div>
                      <PreviousAssessmentStatus assessmentId={assessment.id} />
                    </div>
                  );
                })}
              </div>

              <div className="min-w-0">
                {selectedPreviousAssessmentId ? (
                  <ClinicalExaminationDetails
                    key={selectedPreviousAssessmentId}
                    assessmentId={selectedPreviousAssessmentId}
                    showStatus
                  />
                ) : (
                  <Card className="p-6 text-center text-gray-500 sm:p-8">
                    {t("assessmentList.selectAssessment")}
                  </Card>
                )}
              </div>
            </div>
          ) : (
            <Card className="p-6 text-center text-gray-500 sm:p-10">
              {t("assessmentList.noPrevious")}
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AssessmentList;
